import React, {CSSProperties, forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState} from "react";
import useSWRInfinite from "swr/infinite";
import "./list.css";
import {objToQueryString} from "../../../common/fns";
import {GetListResponse} from "../../../api/apiTypes";
import {fetcher} from "../../../services/http.service";
import {FixedSizeList} from 'react-window';
import Lottie from "lottie-react";
import spinner from "../../../assets/lotties/spinner.json";

export interface InfiniteListProps<T> {
  params?: any;
  url?: string | null;
  data?: T[];
  height?: number;
  itemSize?: number;
  shouldRender?: (item: T) => boolean;
  renderItem: (item: T, style: CSSProperties) => React.ReactNode;
  onGetCount?: (count: number, data: any[]) => void;
  onRefresh?: () => void;
}

function InfiniteList<T>({
                           renderItem,
                           data: dataProp,
                           url,
                           shouldRender,
                           params,
                           height = 200,
                           itemSize = 20,
                           onRefresh,
                           onGetCount
                         }: InfiniteListProps<T>, ref) {
  const [listData, setListData] = useState<T[]>([]);
  const paramString = useMemo(() => objToQueryString(params), [params]);
  
  const getKey = (pageIndex: number, previousPageData?: GetListResponse) => {
    console.log("getKey ", pageIndex, url, paramString);
    if (pageIndex === 0 && url) {
      if (paramString) {
        if (url.includes("?")) return `${url}&${paramString}`;
        return `${url}?${paramString}`;
      }
      return url;
    }
    if (previousPageData && previousPageData.next) {
      return previousPageData.next;
    }
    return null; // reached the end
  };
  
  const {
          data, setSize, mutate, isLoading
        } = useSWRInfinite<GetListResponse>(getKey, fetcher, {revalidateFirstPage: true});
  
  const requestRefresh = useCallback(() => {
    mutate();
    if (onRefresh) {
      onRefresh();
    }
  }, [mutate, onRefresh]);
  
  useImperativeHandle(ref, () => ({
    requestRefresh,
  }), [requestRefresh],);
  
  useEffect(() => {
    let listData: T[] = [];
    if (dataProp) {
      listData = dataProp;
    }
    else if (data && data?.length > 0) {
      data.forEach((item) => {
        if (item?.results) {
          const filtered = shouldRender ? item?.results?.filter(plan => shouldRender(plan)) : item?.results;
          listData = listData.concat(filtered);
        }
      });
    }
    !!onGetCount && onGetCount(data && data?.length > 0 ? data[0]?.count : 0, listData);
    setListData(listData);
  }, [data, dataProp, onGetCount, setSize, shouldRender, url]);
  
  const loadMore = useCallback(() => {
    console.log("infinite list load data:", data)
    if (data && data?.length > 0) {
      const lastRes = data[data?.length - 1];
      if (lastRes && lastRes.current_page * lastRes.items_per_page < lastRes.count) {
        setSize((prev) => prev + 1);
      }
    }
  }, [data, setSize]);
  
  const renderRow = ({index, style}: {
    index: number;
    style: CSSProperties
  }) => {
    const item = listData[index];
    if (item) return renderItem(item, style)
    return null;
  };
  
  return (<div className={"list-container"}>
    <FixedSizeList
      height={height}
      itemCount={listData.length + 1}
      itemSize={itemSize}
      width="100%"
      className={"list"}
      onItemsRendered={({visibleStopIndex}) => {
        if (!dataProp && visibleStopIndex === listData.length - 1) {
          loadMore();
        }
      }}
    >
      {renderRow}
    </FixedSizeList>
    {isLoading
      ? <Lottie autoplay className={"spinner"} animationData={spinner} loop style={{height: 25}} />
      : (!listData.length && <span className={"nodata"}>데이터가 없습니다</span>)}
  </div>);
}

export default forwardRef(InfiniteList) as <T>(props: InfiniteListProps<T> & {
  ref?: React.ForwardedRef<HTMLUListElement>
}) => ReturnType<typeof InfiniteList>;